import { debounce } from "../../../../core/helpers/functional-util.js";

/**
 * Part of the SuggestionAction responsible for rendering the list.
 *
 * @param {string} [activeClass] - css class to add when a .suggestion-item element is active / selected
 * @param {function(object):string} [suggestionToLabel] - Callback that returns the value to label the items
 * @param {string} [suggestionTextClass] - css class to add to "#Mentioned Text" in editor
 * @param {number} [delay] - Debounce timeout for get suggestion list when user types continuously
 * @param {function(string):object[]} getFilteredSuggestions - Function retrieving the filtered suggestions
 *   given a text against which to match
 * @param {function(object): void} selectCurrentHighlightedSuggestion - Function to select the currently highlighted
 *   suggestion from the suggestion list
 */
export default class SuggestionRenderer {
	constructor({
		activeClass = "suggestion-item-active",
		suggestionToLabel = (suggestion) => suggestion.toString(),
		suggestionTextClass,
		delay,
		getFilteredSuggestions,
		selectCurrentHighlightedSuggestion,
	}) {
		this._container = document.createElement("div");

		this._activeClass = activeClass;
		this._suggestionToLabel = suggestionToLabel;
		this._suggestionTextClass = suggestionTextClass;
		this._getFilteredSuggestions = getFilteredSuggestions;

		this._selectCurrentHighlightedSuggestion =
			selectCurrentHighlightedSuggestion;

		this._showListTimeoutId = null;

		this._debouncedGetSuggestions = debounce({
			fn: this._getSuggestions.bind(this),
			delay,
		});
	}

	getView({ key }) {
		return {
			update: (view) => {
				const state = key.getState(view.state);
				if (!state.text) {
					this._hideList();
					clearTimeout(this._showListTimeoutId);
					return;
				}

				this._showListTimeoutId = this._debouncedGetSuggestions({
					view,
					state,
				});
			},
		};
	}

	handleKeyDown({ view, event, state }) {
		const down = event.keyCode === 40;
		const up = event.keyCode === 38;
		const enter = event.keyCode === 13;
		const space = event.keyCode === 32;
		const esc = event.keyCode === 27;

		if (down) {
			this._goNext(state);
			return true;
		} else if (up) {
			this._goPrev(state);
			return true;
		} else if (enter || space) {
			this._selectCurrentHighlightedSuggestion({ view, state });
			return true;
		} else if (esc) {
			clearTimeout(this._showListTimeoutId);
			this._hideList();
			return true;
		}

		return false;
	}

	_getSuggestionsHTML({ suggestions }) {
		const suggestionsHTML = suggestions
			.map((suggestion) => this._getSuggestionHTML({ suggestion }))
			.join("");

		return `<div class="suggestion-item-list">${suggestionsHTML}</div>`;
	}

	_getSuggestionHTML({ suggestion }) {
		return `<div class="suggestion-item">${this._suggestionToLabel(
			suggestion,
		)}</div>`;
	}

	_getSuggestions({ view, state }) {
		const suggestions = this._getFilteredSuggestions(state.text);

		if (!suggestions) {
			return;
		}

		state.suggestions = suggestions;
		if (!state.suggestions) {
			return;
		}

		this._showList({ view, state });
	}

	_showList({ view, state }) {
		this._container.innerHTML = this._getSuggestionsHTML({
			suggestions: state.suggestions,
		});

		this._container
			.querySelectorAll(".suggestion-item")
			.forEach((itemNode, index) => {
				itemNode.addEventListener("click", () => {
					this._selectCurrentHighlightedSuggestion({ view, state });
					view.focus();
				});

				itemNode.addEventListener("mouseover", () => {
					this._setIndex({ index, state });
				});

				itemNode.addEventListener("mouseout", () => {
					this._setIndex({ index, state });
				});
			});

		this._addClassAtIndex(state.index);

		const node = view.domAtPos(view.state.selection.$from.pos);

		let paraDOM = node.node;

		if (paraDOM && !paraDOM.querySelector) {
			paraDOM = paraDOM.parentElement;
		}

		const textDOM = paraDOM?.querySelector(`.${this._suggestionTextClass}`);

		document.body.appendChild(this._container);
		this._container.classList.add("suggestion-item-container");
		this._container.style.position = "fixed";
		this._container.style.display = "block";
		this._container.style.zIndex = "999999";

		if (!textDOM) {
			return;
		}

		const offset = textDOM.getBoundingClientRect();
		const top = textDOM.offsetHeight + offset.top;
		this._container.style.top = `${top}px`;
		this._container.style.left = `${offset.left}px`;
	}

	_hideList() {
		this._container.style.display = "none";
	}

	_setIndex({ index, state }) {
		this._removeClassAtIndex(state.index);
		state.index = index;
		this._addClassAtIndex(state.index);
	}

	_goNext(state) {
		this._removeClassAtIndex(state.index);
		state.index++;

		state.index =
			state.index === state.suggestions.length ? 0 : state.index;

		this._addClassAtIndex(state.index);
	}

	_goPrev(state) {
		this._removeClassAtIndex(state.index);
		state.index--;

		state.index =
			state.index === -1 ? state.suggestions.length - 1 : state.index;

		this._addClassAtIndex(state.index);
	}

	_removeClassAtIndex(index) {
		const itemList = this._container.querySelector(
			".suggestion-item-list",
		).childNodes;
		const prevItem = itemList[index];
		prevItem?.classList.remove(this._activeClass);
	}

	_addClassAtIndex(index) {
		const itemList = this._container.querySelector(
			".suggestion-item-list",
		).childNodes;
		const prevItem = itemList[index];
		prevItem?.classList.add(this._activeClass);
	}
}
